import React, { useEffect, useState } from "react";
import { Search, MapPin } from "lucide-react";
import api from "@/lib/api";
import { onLanguageChange, translateTexts } from "@/lib/i18n";

const KEY = "gs_hero_pick";
export const HERO_FADE = { WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,.35) 18%, #000 48%)", maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,.35) 18%, #000 48%)" };

function pickImage(images, hours = 2) {
  const TWO_H = Math.max(0.1, Number(hours) || 2) * 60 * 60 * 1000;
  if (!images.length) return null;
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || "null");
    const hit = s && Date.now() - s.ts < TWO_H && images.find((i) => i.url === s.url);
    if (hit) return hit;
  } catch { /* ignore */ }
  const prev = (() => { try { return JSON.parse(localStorage.getItem(KEY) || "null")?.url; } catch { return null; } })();
  const pool = images.length > 1 ? images.filter((i) => i.url !== prev) : images;
  const img = pool[Math.floor(Math.random() * pool.length)];
  localStorage.setItem(KEY, JSON.stringify({ url: img.url, ts: Date.now() }));
  return img;
}

function useTypewriter(words) {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    if (!words.length) return undefined;
    const w = words[i % words.length];
    const t = setTimeout(() => {
      if (!del && n < w.length) setN(n + 1);
      else if (!del) setDel(true);
      else if (n > 0) setN(n - 1);
      else { setDel(false); setI(i + 1); }
    }, !del && n === w.length ? 1600 : del ? 45 : 95);
    return () => clearTimeout(t);
  }, [i, n, del, words]);
  const cur = words.length ? words[i % words.length] : "";
  return { text: cur.slice(0, n), idx: i % (words.length || 1) };
}

export function HeroBanner({ q, setQ, onSubmit }) {
  const [hero, setHero] = useState(null);
  const [img, setImg] = useState(null);
  useEffect(() => {
    api.get("/hero").then((r) => { setHero(r.data); setImg(pickImage(r.data.images || [], r.data.interval_hours)); }).catch(() => {});
  }, []);
  const lines = hero?.lines || [];
  const [tw, setTw] = useState(null);
  useEffect(() => {
    if (!hero) return undefined;
    const run = () => translateTexts([hero.prefix || "Gjej", ...(hero.lines || []).map((l) => l.word)]).then((r) => setTw(r));
    run();
    return onLanguageChange(run);
  }, [hero]);
  const words = React.useMemo(() => (tw ? tw.slice(1) : lines.map((l) => l.word)), [hero, tw]);
  const { text, idx } = useTypewriter(words);
  const color = lines[idx]?.color || "#10B981";

  return (
    <section data-testid="home-hero" className="relative overflow-hidden bg-white">
      {img && (
        <div className="absolute inset-y-0 right-0 w-[72%]">
          <img data-testid="hero-image" src={img.url} alt={img.city} className="w-full h-full object-cover object-right" style={HERO_FADE} />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent" />
        </div>
      )}
      <div className="relative px-4 pt-6 pb-4">
        <h1 data-testid="hero-typewriter" data-no-translate className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight min-h-[2.8rem] sm:min-h-[3.5rem]">
          {tw ? tw[0] : hero?.prefix || "Gjej"} <span style={{ color }}>{text}</span><span className="gs-caret" style={{ color }}>|</span>
        </h1>
        <div data-testid="hero-tagline" className="flex items-center gap-1.5 mt-3 text-base font-bold text-slate-800">
          <MapPin className="w-5 h-5 fill-yellow-400 text-yellow-500" />{hero?.tagline || "Afër teje • Diasporë"}
        </div>
        {img?.city && <div data-testid="hero-city" className="absolute right-3 top-3 text-[11px] font-semibold text-white bg-black/35 backdrop-blur px-2 py-0.5 rounded-full">{img.city}</div>}
        <form onSubmit={onSubmit} className="mt-16">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input data-testid="home-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={hero?.placeholder || "Kërko biznes, shërbim, vend ose produkt..."}
              className="w-full bg-white rounded-full pl-12 pr-16 h-14 text-sm shadow-lg border border-slate-100 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none" />
            <button type="submit" data-testid="home-search-submit" aria-label="Kërko" className="absolute right-1.5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center active:scale-95 transition-transform">
              <Search className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default HeroBanner;
